import { fetchMaterialContentBlob } from '../../../features/materials/api';
import type { MaterialData } from './types';

/**
 * コンテンツタイプから適切なファイル拡張子を決定
 */
export function getFileExtension(contentType: string, blob?: Blob): string {
  const type = contentType || blob?.type || 'image/png';

  if (type.startsWith('video/')) {
    if (type.includes('mp4')) return 'mp4';
    if (type.includes('webm')) return 'webm';
    if (type.includes('quicktime') || type.includes('mov')) return 'mov';
    return 'mp4'; // デフォルト
  }

  if (type.startsWith('audio/')) {
    if (type.includes('mpeg') || type.includes('mp3')) return 'mp3';
    if (type.includes('wav')) return 'wav';
    if (type.includes('ogg')) return 'ogg';
    if (type.includes('m4a') || type.includes('mp4')) return 'm4a';
    if (type.includes('aac')) return 'aac';
    return 'mp3'; // デフォルト
  }

  if (type.startsWith('image/')) {
    if (type.includes('jpeg') || type.includes('jpg')) return 'jpg';
    if (type.includes('png')) return 'png';
    if (type.includes('webp')) return 'webp';
    if (type.includes('gif')) return 'gif';
    return 'png'; // デフォルト
  }

  return 'png';
}

/**
 * ASIN値を文字列に正規化（検索・表示用）
 */
export function normalizeAsinForDisplay(asin?: string | string[]): string {
  if (!asin) return '';
  if (typeof asin === 'string') return asin;
  // 配列の場合は最初の1つだけを使用
  return asin[0] || '';
}

/**
 * タイムスタンプ文字列を生成（YYYYMMDDHHMMSS形式）
 */
export function generateTimestamp(): string {
  const now = new Date();
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
}

/**
 * 素材情報からダウンロード用のファイル名を生成
 * 素材に名前 (name) が付いていればそれを最優先で使う。
 */
export function generateDownloadFilename(
  material: MaterialData,
  contentType: string,
  extension: string
): string {
  const asinPart = normalizeAsinForDisplay(material.asin);
  const sourceType = material.source_type || 'material';
  const timestamp = generateTimestamp();

  const metadata = (material as Record<string, unknown>).generation_metadata as Record<string, string> | undefined;

  // ユーザーが付けた素材名はスクリプト/曲名/ASIN より優先する
  const materialName = (material.name ?? '').replace(/[\\/:*?"<>|]/g, '').trim().slice(0, 80);
  if (materialName) {
    return `${materialName}.${extension}`;
  }

  let baseName = '';
  if (sourceType === 'narration' && metadata?.narration_text) {
    const text = metadata.narration_text.replace(/[\\/:*?"<>|]/g, '').trim().slice(0, 30);
    baseName = text || `narration_${timestamp}`;
  } else if (sourceType === 'bgm' && metadata?.song_title) {
    const title = metadata.song_title.replace(/[\\/:*?"<>|]/g, '').trim().slice(0, 30);
    baseName = title || `bgm_${timestamp}`;
  } else if (contentType.startsWith('audio/')) {
    baseName = `audio_${timestamp}`;
  } else if (asinPart) {
    baseName = `${asinPart}_${timestamp}`;
  } else if (sourceType === 'veo_gcs') {
    baseName = `genvideo_${timestamp}`;
  } else if (contentType.startsWith('video/')) {
    baseName = `video_${timestamp}`;
  } else {
    baseName = `img_${timestamp}`;
  }

  return `${baseName}.${extension}`;
}

/**
 * URLからファイルをダウンロードしてブラウザに保存
 *
 * fallbackMaterialId を渡すと、直 fetch に失敗したとき API 経由の中継取得に
 * フォールバックする (CORS 未設定の GCS バケット対策)。
 */
export async function downloadFile(
  url: string,
  filename: string,
  fallbackMaterialId?: string,
): Promise<void> {
  let blob: Blob;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('ファイルの取得に失敗しました');
    }
    blob = await response.blob();
  } catch (error) {
    if (!fallbackMaterialId) throw error;
    console.warn(`[Download] Direct fetch failed for ${fallbackMaterialId}, falling back to API proxy:`, error);
    blob = await fetchMaterialContentBlob(fallbackMaterialId);
  }

  const blobUrl = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}

/**
 * 素材をダウンロード（高レベル関数）
 */
export async function downloadMaterial(
  material: MaterialData,
  downloadUrl: string
): Promise<void> {
  let blob: Blob;
  try {
    const response = await fetch(downloadUrl);
    if (!response.ok) {
      throw new Error('ファイルの取得に失敗しました');
    }
    blob = await response.blob();
  } catch (error) {
    // 署名付きURLの直fetchは、バケットにブラウザ用のCORS設定が無いパートナー
    // (例: gs://saite-generated) では必ず失敗する。動画・音声はすべて GCS 由来なので
    // この経路が無いと素材モーダルからのDLが丸ごと落ちる。API 経由の中継で取り直す。
    // (再生は <video src> で CORS 不要なため、再生できるのにDLだけ失敗する)
    console.warn(`[Download] Direct fetch failed for ${material.id}, falling back to API proxy:`, error);
    blob = await fetchMaterialContentBlob(material.id);
  }

  const contentType = material.content_type || blob.type || 'image/png';
  const extension = getFileExtension(contentType, blob);
  const filename = generateDownloadFilename(material, contentType, extension);

  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}
